import { useCallback, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { ageOn, filledSportFields, isPaused, legacySportKeys, pluralYears } from '../data/athletes'
import {
  useAthlete,
  useAthleteAttendance,
  useClubContext,
  useGroups,
  useInvalidateAthlete,
  useSportFields,
} from '../data/hooks'
import { addDays, formatDayShort, isoDate, parseIsoDate } from '../data/schedule'
import { SliceApiError, type Athlete } from '../data/types'
import { AthleteEvents } from './AthleteEvents'
import { NotesSection } from './NotesScreens'
import { STATUS_LABEL } from './SessionScreen'
import { formatTimeOfDay } from '../../utils/attendance'

type Tab = 'overview' | 'attendance' | 'notes'
const TABS: [Tab, string][] = [
  ['overview', 'Обзор'],
  ['attendance', 'Посещения'],
  ['notes', 'Записи'],
]

/** «Назад» из карточки - туда, откуда пришли; по прямой ссылке - в список. */
export function useBackTo(fallback: string) {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate(fallback, { replace: true })
  }, [fallback, location.key, navigate])
}

/**
 * S08 «Карточка спортсмена»: просмотр, а не форма. Главная задача - найти
 * контакт и спортивный статус. Редактирование отдельно, удаление в меню.
 */
export function AthleteScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { now, api, toast } = useEnv()
  const back = useBackTo('/athletes')
  const athlete = useAthlete(id)
  const groups = useGroups()
  const club = useClubContext()
  const fields = useSportFields(club.data?.sportName)
  const invalidate = useInvalidateAthlete()
  const [menu, setMenu] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [pausing, setPausing] = useState(false)

  const tab: Tab = (['overview', 'attendance', 'notes'] as const).find((t) => t === params.get('tab')) ?? 'overview'
  const setTab = (t: Tab) => setParams(t === 'overview' ? {} : { tab: t }, { replace: true })

  if (athlete.isPending) {
    return (
      <Screen title={' '} back={back}>
        <Skeleton rows={3} label="Загружаю карточку" />
      </Screen>
    )
  }
  if (athlete.isError) {
    const notFound = (athlete.error as { kind?: string })?.kind === 'not_found'
    return (
      <Screen title="Спортсмен" back={back}>
        {notFound ? (
          <EmptyState title="Спортсмен не найден" text="Возможно, его удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть карточку" onRetry={() => void athlete.refetch()} />
        )}
      </Screen>
    )
  }

  const a = athlete.data
  const groupName = a.group_id ? groups.data?.find((g) => g.id === a.group_id)?.name : undefined
  const age = ageOn(a.date_of_birth, now())
  const subtitle = [age !== null ? pluralYears(age) : null, groupName ?? 'Группа не назначена', club.data?.directionName]
    .filter(Boolean)
    .join(' / ')
  const badges = filledSportFields(a, fields.data ?? [])

  const paused = isPaused(a)
  const pausedSince = parseIsoDate(a.paused_at?.slice(0, 10))

  const setPaused = async (next: boolean) => {
    setMenu(false)
    setPausing(true)
    try {
      await api.updateAthlete(a.id, { paused: next })
      invalidate(a.id)
      toast(next ? `${a.name}: на паузе` : `${a.name}: снова занимается`)
    } catch (e) {
      toast(
        e instanceof SliceApiError && e.kind === 'limit'
          ? 'Не вернуть: в тарифе нет свободного места. Поставьте кого-то на паузу или смените тариф.'
          : 'Не удалось сохранить. Попробуйте ещё раз.',
      )
    } finally {
      setPausing(false)
    }
  }

  const remove = async () => {
    setDeleting(true)
    try {
      await api.deleteAthlete(a.id)
      invalidate(a.id)
      toast(`Карточка удалена: ${a.name}`)
      navigate('/athletes', { replace: true })
    } catch {
      setDeleting(false)
      toast('Не удалось удалить. Попробуйте ещё раз.')
    }
  }

  return (
    <Screen
      title={a.name}
      subtitle={subtitle}
      back={back}
      hubContext={{ label: a.name, title: a.name, detail: 'Спортсмен', ref: { kind: 'athlete', athleteId: a.id } }}
      headerAction={
        <button type="button" className="pc-icon-btn" aria-label="Действия" onClick={() => setMenu(true)}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
      }
    >
      {paused && (
        <div className="pc-note" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>
            На паузе{pausedSince ? ` с ${formatDayShort(pausedSince)}` : ''}. Остаётся в группе, но в отчёт об оплате и в
            лимит тарифа не входит.
          </span>
          <button type="button" className="pc-btn" disabled={pausing} onClick={() => void setPaused(false)}>
            Вернуть
          </button>
        </div>
      )}

      {badges.length > 0 && (
        <div className="pc-badges" aria-label="Спортивный статус">
          {badges.map(({ field, value }) => (
            <span key={field.field_key} className="pc-badge">
              {field.field_label}: {value}
            </span>
          ))}
        </div>
      )}

      <div className="pc-segments" role="tablist" aria-label="Разделы карточки" style={{ marginTop: 16 }}>
        {TABS.map(([t, label]) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview athlete={a} onEdit={() => navigate(`/athletes/${a.id}/edit`)} />}
      {tab === 'attendance' && <AttendanceTab id={a.id} />}
      {tab === 'notes' && <NotesSection owner={{ kind: 'athlete', id: a.id }} />}

      {menu && (
        <Sheet title={a.name} onClose={() => setMenu(false)}>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            onClick={() => {
              setMenu(false)
              navigate(`/athletes/${a.id}/edit`)
            }}
          >
            Редактировать
          </button>
          <button type="button" className="pc-btn pc-btn--block" disabled={pausing} onClick={() => void setPaused(!paused)}>
            {paused ? 'Снять с паузы' : 'Поставить на паузу'}
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            onClick={() => {
              setMenu(false)
              setConfirmDelete(true)
            }}
          >
            Удалить спортсмена
          </button>
        </Sheet>
      )}

      {confirmDelete && (
        <Sheet title="Удалить спортсмена?" onClose={() => setConfirmDelete(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            {a.name} пропадёт из списков и групп. Отметки посещаемости и оплат останутся в истории.
            {!paused && ' Если просто не ходит какое-то время, лучше поставить на паузу.'}
          </p>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            disabled={deleting}
            onClick={() => void remove()}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmDelete(false)}>
            Отмена
          </button>
        </Sheet>
      )}
    </Screen>
  )
}

function Overview({ athlete: a, onEdit }: { athlete: Athlete; onEdit: () => void }) {
  const navigate = useNavigate()
  const club = useClubContext()
  const fields = useSportFields(club.data?.sportName)
  const filled = filledSportFields(a, fields.data ?? [])
  const legacy = legacySportKeys(a, fields.data ?? [])
  const birth = parseIsoDate(a.date_of_birth?.slice(0, 10))
  const clearance = parseIsoDate(a.medical_clearance?.slice(0, 10))

  const measurements = [a.height ? `Рост ${a.height} см` : null, a.weight ? `вес ${a.weight} кг` : null]
    .filter(Boolean)
    .join(', ')

  return (
    <>
      <section className="pc-card" style={{ marginTop: 16 }} aria-label="Контакт родителя">
        <div className="pc-kv__label">Контакт родителя</div>
        <div className="pc-kv__value" style={{ fontSize: 18 }}>
          {a.parent_phone ? <a href={`tel:${a.parent_phone.replace(/[^\d+]/g, '')}`}>{a.parent_phone}</a> : 'Телефон не указан'}
        </div>
        {a.phone && (
          <div className="pc-kv__value">
            Свой телефон: <a href={`tel:${a.phone.replace(/[^\d+]/g, '')}`}>{a.phone}</a>
          </div>
        )}
      </section>

      <ul className="pc-list" style={{ marginTop: 8 }}>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Дата рождения</div>
            <div className="pc-kv__value">{birth ? `${formatDayShort(birth)} ${birth.getFullYear()}` : 'Не указана'}</div>
          </div>
        </li>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Измерения</div>
            <div className="pc-kv__value">{measurements || 'Рост и вес пока не добавлены'}</div>
          </div>
        </li>
        <li>
          <button type="button" className="pc-list-row" style={{ padding: '12px 4px' }} onClick={() => navigate(`/athletes/${a.id}/sport`)}>
            <span className="pc-list-row__main">
              <span className="pc-kv__label" style={{ display: 'block' }}>
                Квалификация
              </span>
              <span className="pc-kv__value" style={{ display: 'block' }}>
                {filled.length > 0
                  ? filled.map(({ field, value }) => `${field.field_label}: ${value}`).join(', ')
                  : 'Спортивные данные не указаны'}
                {legacy.length > 0 && `. Есть поля из прежней версии: ${legacy.length}`}
              </span>
            </span>
            <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
          </button>
        </li>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Медицинский допуск</div>
            <div className="pc-kv__value">
              {clearance ? `до ${formatDayShort(clearance)} ${clearance.getFullYear()}` : 'Не указан'}
            </div>
          </div>
        </li>
        {a.notes && (
          <li>
            <div className="pc-kv">
              <div className="pc-kv__label">Заметки</div>
              <div className="pc-kv__value" style={{ whiteSpace: 'pre-wrap' }}>
                {a.notes}
              </div>
            </div>
          </li>
        )}
      </ul>

      <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 12 }} onClick={onEdit}>
        Редактировать
      </button>

      <AthleteEvents athleteId={a.id} athleteName={a.name} />
    </>
  )
}

const PERIODS: [number, string][] = [
  [30, '30 дней'],
  [90, '90 дней'],
  [365, 'Год'],
]

function AttendanceTab({ id }: { id: string }) {
  const { now } = useEnv()
  const [days, setDays] = useState(90)
  const to = isoDate(now())
  const from = isoDate(addDays(now(), -days + 1))
  const history = useAthleteAttendance(id, from, to, true)
  const fromDate = parseIsoDate(from)!

  return (
    <section style={{ marginTop: 16 }}>
      <div className="pc-chips" role="group" aria-label="Период">
        {PERIODS.map(([d, label]) => (
          <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)}>
            {label}
          </button>
        ))}
      </div>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        Период: с {formatDayShort(fromDate)} {fromDate.getFullYear()} по сегодня
      </p>
      {history.isPending ? (
        <Skeleton rows={3} label="Загружаю посещения" />
      ) : history.isError ? (
        <ErrorState title="Не удалось загрузить посещения" onRetry={() => void history.refetch()} />
      ) : history.data.items.length === 0 ? (
        <EmptyState title="Отметок за период нет" text="Отмеченные занятия появятся здесь." />
      ) : (
        <>
          <div className="pc-card" style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 20 }}>
              Посещено {history.data.stats.present_count + history.data.stats.late_count + (history.data.stats.left_early_count ?? 0)} из{' '}
              {history.data.stats.total_sessions}
            </strong>
            <div className="pc-muted" style={{ fontSize: 14, marginTop: 4 }}>
              Отмеченных занятий за период. Неотмеченные не считаются.
            </div>
          </div>
          <ul className="pc-list" style={{ marginTop: 8 }}>
            {history.data.items.map((it) => {
              const d = parseIsoDate(it.date.slice(0, 10))
              // Сервер отдаёт время дня как «0000-01-01T17:00:00Z»: нужны ЧЧ:ММ, а не первые символы
              const time = formatTimeOfDay(it.training_start_time)
              return (
                <li key={it.id}>
                  <div className="pc-list-row" style={{ cursor: 'default' }}>
                    <span className="pc-list-row__main">
                      {d ? `${formatDayShort(d)}` : it.date}
                      {time ? `, ${time}` : ''}
                    </span>
                    <span className="pc-list-row__aside">{STATUS_LABEL[it.status]}</span>
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}
